import React, { useState, useEffect } from 'react';
import './EditorStyles.css';

const ServiceDetailsModal = ({ service, onClose }) => {
  const [detailContent, setDetailContent] = useState({
    description_ar: '',
    description_en: '',
    detail_image_url: ''
  });
  const [features, setFeatures] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [editingGallery, setEditingGallery] = useState(null);

  useEffect(() => {
    if (service) {
      loadServiceDetails();
    }
  }, [service]);

  const loadServiceDetails = async () => {
    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/services/${service.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        const serviceData = data.data;
        console.log('Service details loaded:', serviceData); // Debug log
        
        // Set detail content
        if (serviceData.detail) {
          setDetailContent({
            description_ar: serviceData.detail.description_ar || '',
            description_en: serviceData.detail.description_en || '',
            detail_image_url: serviceData.detail.detail_image_url || ''
          });
        }
        
        // Set features
        setFeatures(serviceData.features || []);
        console.log('Features loaded:', serviceData.features); // Debug log
        
        // Set gallery
        setGallery(serviceData.gallery || []);
        console.log('Gallery loaded:', serviceData.gallery); // Debug log
      } else {
        console.error('Failed to load service details:', data.message);
      }
    } catch (error) {
      console.error('Error loading service details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    setMessage('');

    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/services/${service.id}/details`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(detailContent)
      });

      const data = await response.json();
      if (data.success) {
        setMessage('تم حفظ تفاصيل الخدمة بنجاح!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.message || 'حدث خطأ');
      }
    } catch (error) {
      setMessage('خطأ في الاتصال');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        const baseUrl = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://server.acpca.net';
        const imageUrl = `${baseUrl}${data.url.startsWith('/') ? data.url : `/${data.url}`}`;
        setDetailContent(prev => ({ ...prev, [field]: imageUrl }));
        setMessage('تم رفع الصورة بنجاح!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage('خطأ في رفع الصورة');
    }
  };

  const handleDeleteFeature = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الميزة؟')) return;

    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/services/features/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        loadServiceDetails();
        setMessage('تم حذف الميزة بنجاح!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleDeleteGallery = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الصورة؟')) return;

    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/services/gallery/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        loadServiceDetails();
        setMessage('تم حذف الصورة بنجاح!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  if (loading) {
    return (
      <div className="editor-modal-overlay" onClick={onClose}>
        <div className="editor-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="editor-loading">جاري التحميل...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="editor-modal-overlay" onClick={onClose}>
      <div className="editor-modal-content" style={{ maxWidth: '1000px', maxHeight: '95vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="editor-modal-header">
          <h2>تعديل تفاصيل الخدمة: {service.title_ar}</h2>
          <button className="editor-modal-close" onClick={onClose}>×</button>
        </div>

        {message && (
          <div className={`editor-message ${message.includes('نجح') || message.includes('success') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        {/* Detail Content Section */}
        <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
          <h3 style={{ color: '#815327', marginBottom: '20px' }}>محتوى صفحة التفاصيل</h3>
          <div className="editor-grid">
            <div className="editor-column">
              <div className="editor-form-group">
                <label>الوصف (عربي)</label>
                <textarea
                  rows="5"
                  value={detailContent.description_ar}
                  onChange={(e) => setDetailContent({...detailContent, description_ar: e.target.value})}
                />
              </div>
            </div>
            <div className="editor-column">
              <div className="editor-form-group">
                <label>Description (English)</label>
                <textarea
                  rows="5"
                  value={detailContent.description_en}
                  onChange={(e) => setDetailContent({...detailContent, description_en: e.target.value})}
                />
              </div>
            </div>
          </div>
          <div className="editor-form-group">
            <label>صورة صفحة التفاصيل</label>
            <div className="editor-image-input">
              <input
                type="text"
                value={detailContent.detail_image_url}
                onChange={(e) => setDetailContent({...detailContent, detail_image_url: e.target.value})}
                placeholder="رابط الصورة أو رفع ملف"
              />
              <label className="editor-upload-btn">
                رفع صورة
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload('detail_image_url', e.target.files[0])}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
            {detailContent.detail_image_url && (
              <img src={detailContent.detail_image_url} alt="Preview" className="editor-image-preview" />
            )}
          </div>
          <button className="editor-btn-save" onClick={handleSaveDetails} disabled={saving}>
            {saving ? 'جاري الحفظ...' : 'حفظ المحتوى'}
          </button>
        </div>

        {/* Features Section */}
        <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: '#815327', margin: 0 }}>المميزات</h3>
            <button className="editor-btn-add" onClick={() => {
              setEditingFeature(null);
              setShowFeatureModal(true);
            }}>
              + إضافة ميزة
            </button>
          </div>
          <div className="editor-items-list">
            {features.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>لا توجد مميزات</p>
            ) : (
              features.map((feature) => (
                <div key={feature.id} className="editor-item-card">
                  <div className="editor-item-content">
                    <h4>{feature.feature_ar} / {feature.feature_en}</h4>
                    <p><small>الترتيب: {feature.display_order}</small></p>
                  </div>
                  <div className="editor-item-actions">
                    <button className="editor-btn-sm editor-btn-edit" onClick={() => {
                      setEditingFeature(feature);
                      setShowFeatureModal(true);
                    }}>
                      تعديل
                    </button>
                    <button className="editor-btn-sm editor-btn-delete" onClick={() => handleDeleteFeature(feature.id)}>
                      حذف
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Gallery Section */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: '#815327', margin: 0 }}>معرض الصور</h3>
            <button className="editor-btn-add" onClick={() => {
              setEditingGallery(null);
              setShowGalleryModal(true);
            }}>
              + إضافة صورة
            </button>
          </div>
          <div className="editor-items-list">
            {gallery.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>لا توجد صور</p>
            ) : (
              gallery.map((img) => (
                <div key={img.id} className="editor-item-card">
                  <div className="editor-item-content">
                    <img 
                      src={img.image_url && (img.image_url.startsWith('http') ? img.image_url : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://server.acpca.net'}${img.image_url.startsWith('/') ? img.image_url : `/${img.image_url}`}`)} 
                      alt="Gallery" 
                      style={{maxWidth: '150px', borderRadius: '4px', marginBottom: '10px'}}
                    />
                    <p><small>الترتيب: {img.display_order}</small></p>
                  </div>
                  <div className="editor-item-actions">
                    <button className="editor-btn-sm editor-btn-edit" onClick={() => {
                      setEditingGallery(img);
                      setShowGalleryModal(true);
                    }}>
                      تعديل
                    </button>
                    <button className="editor-btn-sm editor-btn-delete" onClick={() => handleDeleteGallery(img.id)}>
                      حذف
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Feature Modal */}
        {showFeatureModal && (
          <FeatureModal
            serviceId={service.id}
            feature={editingFeature}
            onClose={() => {
              setShowFeatureModal(false);
              setEditingFeature(null);
            }}
            onSave={() => {
              loadServiceDetails();
              setShowFeatureModal(false);
              setEditingFeature(null);
            }}
          />
        )}

        {/* Gallery Modal */}
        {showGalleryModal && (
          <GalleryModal
            serviceId={service.id}
            image={editingGallery}
            onClose={() => {
              setShowGalleryModal(false);
              setEditingGallery(null);
            }}
            onSave={() => {
              loadServiceDetails();
              setShowGalleryModal(false);
              setEditingGallery(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Feature Modal Component
const FeatureModal = ({ serviceId, feature, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    feature_ar: '',
    feature_en: '',
    display_order: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (feature) {
      setFormData({
        feature_ar: feature.feature_ar || '',
        feature_en: feature.feature_en || '',
        display_order: feature.display_order || 0
      });
    }
  }, [feature]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('dashboard_token');
      const url = feature
        ? `${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/services/features/${feature.id}`
        : `${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/services/${serviceId}/features`;
      
      const method = feature ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        onSave();
      }
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="editor-modal-overlay" onClick={onClose}>
      <div className="editor-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="editor-modal-header">
          <h2>{feature ? 'تعديل الميزة' : 'إضافة ميزة جديدة'}</h2>
          <button className="editor-modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="editor-grid">
            <div className="editor-column">
              <div className="editor-form-group">
                <label>الميزة (عربي) *</label>
                <input
                  type="text"
                  value={formData.feature_ar}
                  onChange={(e) => setFormData({...formData, feature_ar: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="editor-column">
              <div className="editor-form-group">
                <label>Feature (English) *</label>
                <input
                  type="text"
                  value={formData.feature_en}
                  onChange={(e) => setFormData({...formData, feature_en: e.target.value})}
                  required
                />
              </div>
            </div>
          </div>
          <div className="editor-form-group">
            <label>ترتيب العرض</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
            />
          </div>
          <div className="editor-actions">
            <button type="submit" className="editor-btn-save" disabled={saving}>
              {saving ? 'جاري الحفظ...' : (feature ? 'حفظ التغييرات' : 'إضافة الميزة')}
            </button>
            <button type="button" className="editor-btn-secondary" onClick={onClose}>
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Gallery Modal Component
const GalleryModal = ({ serviceId, image, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    image_url: '',
    display_order: 0
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (image) {
      setFormData({
        image_url: image.image_url || '',
        display_order: image.display_order || 0
      });
    }
  }, [image]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        const baseUrl = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://server.acpca.net';
        const imageUrl = `${baseUrl}${data.url.startsWith('/') ? data.url : `/${data.url}`}`;
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url) {
      alert('يرجى إضافة رابط الصورة');
      return;
    }
    setSaving(true);

    try {
      const token = localStorage.getItem('dashboard_token');
      const url = image
        ? `${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/services/gallery/${image.id}`
        : `${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/services/${serviceId}/gallery`;
      
      const method = image ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        onSave();
      }
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="editor-modal-overlay" onClick={onClose}>
      <div className="editor-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="editor-modal-header">
          <h2>{image ? 'تعديل الصورة' : 'إضافة صورة جديدة'}</h2>
          <button className="editor-modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="editor-form-group">
            <label>رابط الصورة *</label>
            <div className="editor-image-input">
              <input
                type="text"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="رابط الصورة أو رفع ملف"
                required
              />
              <label className="editor-upload-btn">
                {uploading ? 'جاري الرفع...' : 'رفع صورة'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
              </label>
            </div>
            {formData.image_url && (
              <img src={formData.image_url} alt="Preview" className="editor-image-preview" />
            )}
          </div>
          <div className="editor-form-group">
            <label>ترتيب العرض</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
            />
          </div>
          <div className="editor-actions">
            <button type="submit" className="editor-btn-save" disabled={saving || uploading}>
              {saving ? 'جاري الحفظ...' : (image ? 'حفظ التغييرات' : 'إضافة الصورة')}
            </button>
            <button type="button" className="editor-btn-secondary" onClick={onClose}>
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceDetailsModal;

